import { get } from "lodash";
import React, { useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import {
  visitorId,
  setCartItemsCount,
  selectCartItemsCount,
} from "../features/cart/cartSlice";
import { REMOVE_FROM_CART_MUTATION } from "../graphql/mutations/removeFromCartMutation";
import { startLoading, stopLoading } from "../features/loading/loadingSlice";
import ImageWithFallback from "./ImageWithFallback";
import { Popover, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { isAuth } from "../features/userLogin/userLoginSlice";

const GetCartItems = `
query CartItems($visitorId: String!)  {
  cartItems (visitor_id: $visitorId)  {
      category_id
      category_name
      category {
          id
          name
          slug
      }
      items {
          id
          visitor_id
          product_id
          variant_id
          qty
          product {
              id
              name
              slug
              main_image_url
              price
              final_price
              delivery_date
          }
          variant {
              id
              name
              sku
              price
              final_price
          }
      }
      children {
          category_id
          category_name
          items {
              id
              visitor_id
              product_id
              variant_id
              qty
              product {
                  id
                  name
                  slug
                  main_image_url
                  price
                  final_price
                  delivery_date
              }
              variant {
                  id
                  name
                  sku
                  price
                  final_price
              }
          }
          children {
              category_id
              category_name
              items {
                  id
                  visitor_id
                  product_id
                  variant_id
                  qty
                  product {
                      id
                      name
                      slug
                      main_image_url
                      price
                      final_price
                      delivery_date
                  }
                  variant {
                      id
                      name
                      sku
                      price
                      final_price
                  }
              }
          }
      }
  }
  }
`;

// Helper function to flatten all items from category groups
const flattenItems = (groups: any[]): any[] => {
  const result: any[] = [];
  groups.forEach((group: any) => {
    if (group.items && group.items.length > 0) {
      result.push(...group.items);
    }
    if (group.children && group.children.length > 0) {
      result.push(...flattenItems(group.children));
    }
  });
  return result;
};

interface CartPopupProps {
  children: React.ReactNode;
}

export const CartPopup = ({ children }: CartPopupProps) => {
  const dispatch = useAppDispatch();
  const isUserLoggedIn = useAppSelector(isAuth);
  const currentVisitorId = useAppSelector(visitorId);
  const [{ fetching, data }, reexecuteQuery] = useQuery({
    query: GetCartItems,
    variables: { visitorId: currentVisitorId },
    requestPolicy: "network-only",
    pause: !isUserLoggedIn || !currentVisitorId,
  });
  const [, removeFromCart] = useMutation(REMOVE_FROM_CART_MUTATION);

  // Track the last count we dispatched to avoid redundant updates
  const lastDispatchedCountRef = useRef<number | null>(null);

  // Handle cart click to reload data
  const handleCartIconClick = () => {
    if (currentVisitorId) {
      reexecuteQuery({ requestPolicy: "network-only" });
    }
  };

  // Flatten all items from grouped structure
  const allCartItems = useMemo(() => {
    if (!data || !data.cartItems) return [];
    return flattenItems(get(data, "cartItems", []));
  }, [data]);

  // Update cart count when data changes, but only if it's different to prevent unnecessary re-renders
  // Use queueMicrotask to defer state updates until after the current render cycle
  // This prevents the "Cannot update component during render" warning
  useEffect(() => {
    if (!isUserLoggedIn || !currentVisitorId) {
      // If not logged in or no visitor ID, reset count to 0
      if (lastDispatchedCountRef.current !== 0) {
        lastDispatchedCountRef.current = 0;
        queueMicrotask(() => {
          dispatch(setCartItemsCount(0));
        });
      }
    } else if (data && data.cartItems !== undefined) {
      // Calculate count directly from data to avoid dependency on allCartItems array reference
      // This prevents the array reference from changing unnecessarily
      const items = flattenItems(get(data, "cartItems", []));
      const count = items.length;
      // Only dispatch if the count has actually changed from what we last dispatched
      if (lastDispatchedCountRef.current !== count) {
        lastDispatchedCountRef.current = count;
        // Use queueMicrotask to defer the dispatch until after the current render cycle
        queueMicrotask(() => {
          dispatch(setCartItemsCount(count));
        });
      }
    }
  }, [data, currentVisitorId, isUserLoggedIn, dispatch]);

  const handleRemove = async (
    e: React.MouseEvent,
    productId: string,
    variantId?: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentVisitorId) return;
    const variables: any = {
      visitor_id: currentVisitorId,
      product_id: productId,
    };
    if (variantId) variables.variant_id = variantId;
    dispatch(startLoading());
    await removeFromCart(variables);
    dispatch(stopLoading());
    // Giữ lại data cũ trong lúc fetch data mới
    reexecuteQuery({ requestPolicy: "cache-and-network" });
  };

  // Calculate cart total
  const cartTotal = useMemo(() => {
    if (allCartItems.length === 0) return 0;
    return allCartItems.reduce((total: number, item: any) => {
      const variantPrice =
        get(item, "variant.final_price") || get(item, "variant.price");
      const productPrice =
        get(item, "product.final_price") || get(item, "product.price");
      const unitPrice = variantPrice || productPrice || 0;
      const qty = get(item, "qty", 1);
      return total + unitPrice * qty;
    }, 0);
  }, [allCartItems]);

  const cartItemsCount = useMemo(() => {
    return allCartItems.length;
  }, [allCartItems]);

  // Get first 5 items for mini cart
  const cartItems = useMemo(() => {
    return allCartItems.slice(0, 5);
  }, [allCartItems]);

  return (
    <Popover className="relative">
      {({ open }) => {
        return (
          <>
            <Popover.Button
              className="focus:outline-none border-none bg-transparent p-0"
              as="div"
            >
              <div onClick={handleCartIconClick} className="cursor-pointer">
                {children}
              </div>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute right-0 z-50 mt-2 w-screen max-w-sm transform">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b px-4 py-3 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Giỏ hàng {cartItemsCount > 0 && `(${cartItemsCount})`}
                    </h3>
                    {fetching && (
                      <div className="flex items-center">
                        <svg
                          className="animate-spin h-4 w-4 text-primary-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="max-h-[60vh] overflow-y-auto">
                    {fetching && !data?.cartItems ? (
                      <div className="flex flex-col items-center justify-center py-10">
                        <svg
                          className="animate-spin h-8 w-8 text-primary-600 mb-3"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <p className="text-sm text-gray-500">
                          Đang tải giỏ hàng...
                        </p>
                      </div>
                    ) : cartItemsCount === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 px-4">
                        <p className="text-sm text-gray-500 mb-4">
                          Giỏ hàng của bạn trống
                        </p>
                        <Link
                          to="/"
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Tiếp tục mua sắm
                        </Link>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {cartItems.map((cartItem: any) => {
                          const variantPrice =
                            get(cartItem, "variant.final_price") ||
                            get(cartItem, "variant.price");
                          const productPrice =
                            get(cartItem, "product.final_price") ||
                            get(cartItem, "product.price");
                          const unitPrice = variantPrice || productPrice || 0;
                          const qty = get(cartItem, "qty", 1);
                          const keyId = `${get(cartItem, "product.id")}-${get(
                            cartItem,
                            "variant.id",
                            "basic"
                          )}`;

                          return (
                            <div
                              key={keyId}
                              className="flex items-start space-x-3 p-4 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex-shrink-0">
                                <ImageWithFallback
                                  className="h-16 w-16 object-cover rounded-md border border-gray-200"
                                  src={get(cartItem, "product.main_image_url")}
                                  alt={get(cartItem, "product.name")}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <Link
                                  to={`/product/${get(
                                    cartItem,
                                    "product.slug"
                                  )}`}
                                  className="block"
                                >
                                  <h4 className="text-sm font-medium text-gray-900 line-clamp-2 hover:text-primary-600 transition-colors">
                                    {get(cartItem, "product.name")}
                                  </h4>
                                </Link>
                                {get(cartItem, "variant.id") && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {get(cartItem, "variant.name")}
                                  </p>
                                )}
                                <div className="flex items-center justify-between mt-1.5">
                                  <span className="text-xs text-gray-500">
                                    x{qty}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {(unitPrice * qty).toLocaleString("vi-VN")}₫
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) =>
                                  handleRemove(
                                    e,
                                    get(cartItem, "product.id"),
                                    get(cartItem, "variant.id")
                                  )
                                }
                                className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1"
                                title="Xóa"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                        {cartItemsCount > 5 && (
                          <div className="px-4 py-3 bg-gray-50 text-center">
                            <Link
                              to="/cart"
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                            >
                              Xem thêm {cartItemsCount - 5} sản phẩm khác
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {cartItemsCount > 0 && (
                    <div className="border-t bg-gray-50 px-4 py-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-900">
                          Tổng cộng:
                        </span>
                        <span className="text-base font-bold text-primary-600">
                          {cartTotal.toLocaleString("vi-VN")}₫
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <Link
                          to="/cart"
                          className="flex-1 text-center px-3 py-2 bg-primary-600 border border-gray-300 rounded-md text-sm font-medium text-white hover:bg-gray-100 transition-colors"
                        >
                          Xem giỏ hàng
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        );
      }}
    </Popover>
  );
};
